import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import withStyles from 'isomorphic-style-loader/withStyles';
import InfoTooltip from './InfoTooltip';
import { formatMoney, formatCount } from './reportingFormat';
import { resolveCssColor, THEME_VARS } from './chartTheme';
import s from './OwnerReporting.css';

ChartJS.register(ArcElement, Tooltip);

// Generic donut. segments = [{ key, label, value, cssVar }] (cssVar = a theme variable such as '--primary-link-foreground').
// The share (%) is the ratio of two values returned by the server, for display only. With `currency` the values are money,
// otherwise plain counts (the dashboard reservation split). total / totalLabel are shown in the middle.
const DonutChart = ({ title, help, segments, total, totalLabel, currency }) => {
  const { locale } = useIntl();
  const [colors, setColors] = useState(null);
  const varsKey = segments.map((segment) => segment.cssVar).join('|');
  useEffect(() => {
    setColors({ segments: segments.map((segment) => resolveCssColor(segment.cssVar)), border: resolveCssColor(THEME_VARS.segmentBorder) });
  }, [varsKey]);

  const sum = useMemo(() => segments.reduce((acc, segment) => acc + (Number(segment.value) || 0), 0), [segments]);
  // Shares in whole %, largest remainder method: the displayed shares always add up to 100 (display of counts/values only).
  const shares = useMemo(() => {
    if (sum <= 0) return segments.map(() => 0);
    const exact = segments.map((segment) => ((Number(segment.value) || 0) / sum) * 100);
    const floors = exact.map(Math.floor);
    let missing = 100 - floors.reduce((acc, value) => acc + value, 0);
    exact.map((value, index) => ({ index, rest: value - floors[index] })).sort((a, b) => b.rest - a.rest || a.index - b.index)
      .forEach(({ index }) => { if (missing > 0) { floors[index] += 1; missing -= 1; } });
    return floors;
  }, [segments, sum]);
  const share = (index) => `${shares[index]} %`;
  const show = (value) => (currency ? formatMoney(value, currency, locale) : formatCount(value, locale));

  const data = {
    labels: segments.map((segment) => segment.label),
    datasets: [{
      data: segments.map((segment) => Number(segment.value) || 0),
      backgroundColor: segments.map((_, index) => (colors && colors.segments[index]) || undefined),
      borderColor: (colors && colors.border) || undefined,
      borderWidth: 2,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (context) => `${context.label}: ${show(context.raw)}` } },
    },
  };

  return (
    <div className={s.panel}>
      <h3 className={s.panelTitle}><span className={s.panelTitleText}>{title}<InfoTooltip text={help} /></span></h3>
      <div className={s.donutBody}>
        <div className={s.donutCanvas}>
          {colors && sum > 0 && <Doughnut data={data} options={options} />}
          <div className={s.donutCenter}>
            <span className={s.donutCenterValue}>{show(total)}</span>
            {totalLabel && <span className={s.donutCenterLabel}>{totalLabel}</span>}
          </div>
        </div>
        <ul className={s.legend}>
          {segments.map((segment, index) => (
            <li key={segment.key} className={s.legendItem}>
              <span className={s.legendDot} style={{ background: `var(${segment.cssVar})` }} aria-hidden="true" />
              <span className={s.legendName}>{segment.label}</span>
              <span className={s.legendAmount}>{show(segment.value)}</span>
              <span className={s.legendShare}>({share(index)})</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default withStyles(s)(DonutChart);
